import { useState, useEffect, useCallback, useMemo } from "react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from "recharts";
import { Activity, Users, MessageSquare, Heart, Send, TrendingUp, RefreshCw, Download, ExternalLink, Flame, Zap, Clock, AlertTriangle, UserPlus, MessageCircle, Eye, ChevronRight } from "lucide-react";

export const API_KEY = "patnzcc8iF8UEWUQt.8cebf1f108bb0283f15b6bba9a998e44715d0e4935cdb3024026cdf8f1732893";
const H = { Authorization: `Bearer ${API_KEY}` };
const REFRESH = 5 * 60 * 1000;

// test

async function fetchT(baseUrl, table, filter) {
  let u = `${baseUrl}/${encodeURIComponent(table)}?pageSize=100`;
  if (filter) u += `&filterByFormula=${encodeURIComponent(filter)}`;
  const all = [];
  let off = null;
  do {
    const r = await fetch(off ? `${u}&offset=${off}` : u, { headers: H });
    const d = await r.json();
    if (d.records) all.push(...d.records);
    off = d.offset || null;
  } while (off);
  return all.map(r => ({ _id: r.id, ...r.fields }));
}

function fmt(n) { return n == null ? "—" : n >= 1000 ? (n/1000).toFixed(1)+"k" : n.toLocaleString(); }
function pct(a,b) { return b ? ((a/b)*100).toFixed(1)+"%" : "0%"; }
function sd(d) { return d ? new Date(d).toLocaleDateString("en-IN",{month:"short",day:"numeric"}) : ""; }
function daysBetween(d1,d2) { return Math.floor((new Date(d2)-new Date(d1))/(1000*60*60*24)); }
function csvExport(data,fn) {
  if(!data.length) return;
  const k=Object.keys(data[0]).filter(x=>!x.startsWith('_'));
  const c=[k.join(","),...data.map(r=>k.map(x=>`"${(r[x]??"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
  const a=document.createElement("a"); a.href=window.URL.createObjectURL(new Blob([c],{type:"text/csv"})); a.download=fn; a.click();
}

function getLeadTag(post) {
  const conn = post["Connection Status"] || "";
  const dms = post["DM's"] || "";
  const status = post.Status || "";
  const reply = post["Reply Status"] || "";
  const lastMod = post["Last Modified"] || "";
  if (reply === "Replied" || dms === "Responded") return { tag: "Hot Lead", color: "#EF4444", bg: "#FEF2F2", icon: Flame };
  if (conn === "accepted") return { tag: "Warm Lead", color: "#F59E0B", bg: "#FFFBEB", icon: Zap };
  if (conn === "pending" && lastMod && daysBetween(lastMod, new Date()) >= 2) return { tag: "Stale Lead", color: "#6B7280", bg: "#F3F4F6", icon: AlertTriangle };
  if (status === "Responded" || status === "New") return { tag: "New Lead", color: "#3B82F6", bg: "#EFF6FF", icon: UserPlus };
  return { tag: "Unknown", color: "#9CA3AF", bg: "#F9FAFB", icon: Users };
}

const TABS = ["Overview", "Leads Pipeline", "Connections", "DMs", "Outreach Engine", "Comments"];

function Stat({ icon: I, label, value, sub, color = "#3B82F6" }) {
  return (
    <div className="stat-card" style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 8, transition: "box-shadow .2s", cursor: "default", minWidth: 0 }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.06)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
      <div style={{ background: color + "12", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <I size={17} color={color} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: "#111827", letterSpacing: "-0.03em", wordBreak: "break-word" }}>{value}</div>
        <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 2, wordBreak: "break-word" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3, wordBreak: "break-word" }}>{sub}</div>}
      </div>
    </div>
  );
}

function Card({ title, children, onExport, extra }) {
  return (
    <div className="card" style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: "18px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 650, color: "#111827" }}>{title}</span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {extra}
          {onExport && <button onClick={onExport} style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 7, padding: "3px 9px", color: "#9CA3AF", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", gap: 3 }}><Download size={11}/> CSV</button>}
        </div>
      </div>
      {children}
    </div>
  );
}

function LeadBadge({ post }) {
  const { tag, color, bg, icon: I } = getLeadTag(post);
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, color, background: bg }}><I size={12}/>{tag}</span>;
}

const tt = { contentStyle: { background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, fontSize: 12, color: "#374151", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }, itemStyle: { color: "#6B7280" } };

export default function Dashboard({ baseId, onLogout, username }) {
  const baseUrl = `https://api.airtable.com/v0/${baseId}`;
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [range, setRange] = useState("30d");
  const [data, setData] = useState({ posts: [], connections: [], dms: [], comments: [], relations: [], daily: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [posts, connections, dms, comments, relations, daily] = await Promise.all([
        fetchT(baseUrl, "Posts"), fetchT(baseUrl, "Connection_Requests"), fetchT(baseUrl, "DMs"),
        fetchT(baseUrl, "Comments"), fetchT(baseUrl, "Relations"), fetchT(baseUrl, "Daily_Summary")
      ]);
      setData({ posts, connections, dms, comments, relations, daily });
      setLastRefresh(new Date());
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [baseUrl]);

  useEffect(() => { load(); const t = setInterval(load, REFRESH); return () => clearInterval(t); }, [load]);

  const agg = useMemo(() => {
    const p = data.posts;
    const responded = p.filter(x => x.Status === "Responded").length;
    const newPosts = p.filter(x => x.Status === "New").length;
    const connAcc = p.filter(x => (x["Connection Status"]||"") === "accepted").length;
    const connPend = p.filter(x => (x["Connection Status"]||"") === "pending").length;
    const dmS = data.dms.filter(x => x.direction === "sent").length;
    const dmR = data.dms.filter(x => x.direction === "received").length;
    const cS = data.comments.filter(x => x.direction === "sent").length;
    const cR = data.comments.filter(x => x.direction === "received").length;
    const rel = data.relations.length;
    return { responded, newPosts, connAcc, connPend, dmS, dmR, cS, cR, rel, total: p.length,
      hot: p.filter(x => getLeadTag(x).tag === "Hot Lead").length,
      warm: p.filter(x => getLeadTag(x).tag === "Warm Lead").length,
      stale: p.filter(x => getLeadTag(x).tag === "Stale Lead").length,
      newL: p.filter(x => getLeadTag(x).tag === "New Lead").length };
  }, [data]);

  const chartDaily = useMemo(() => {
    const days = range==="7d"?7:range==="14d"?14:range==="30d"?30:90;
    const cut = new Date(); cut.setDate(cut.getDate()-days);
    return data.daily.filter(r=>r.date&&new Date(r.date)>=cut).sort((a,b)=>new Date(a.date)-new Date(b.date)).map(r=>({...r,dl:sd(r.date)}));
  }, [data.daily, range]);

  const leadDist = useMemo(() => [
    {name:"Hot",value:agg.hot,color:"#EF4444"},{name:"Warm",value:agg.warm,color:"#F59E0B"},
    {name:"New",value:agg.newL,color:"#3B82F6"},{name:"Stale",value:agg.stale,color:"#6B7280"}
  ].filter(x=>x.value>0), [agg]);

  return (
    <div style={{ minHeight: "100vh", background: "#F8F9FB", color: "#111827", fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet"/>

      <div className="dashboard-header" style={{ background: "#fff", borderBottom: "1px solid #E5E7EB", padding: "18px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg, #3B82F6, #8B5CF6)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: 4 }}>
  <img src="/mnt/user-data/uploads/LYNCO_by_100XAI.png" alt="100XAI" style={{ width: "100%", height: "100%", objectFit: "contain", filter: "brightness(0) invert(1)" }} />
</div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 750, margin: 0, letterSpacing: "-0.03em" }}>LYNCO by 100XAI</h1>
            <p style={{ fontSize: 11.5, color: "#9CA3AF", margin: 0 }}>{lastRefresh ? `Refreshed ${lastRefresh.toLocaleTimeString()}` : "Loading…"} · Auto-refresh 5min</p>
          </div>
        </div>
        <div className="header-controls" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 9, overflow: "hidden" }}>
            {["7d","14d","30d","90d"].map(r=><button key={r} onClick={()=>setRange(r)} style={{ padding: "6px 13px", fontSize: 11.5, fontWeight: 600, border: "none", cursor: "pointer", color: range===r?"#3B82F6":"#9CA3AF", background: range===r?"#EFF6FF":"transparent" }}>{r}</button>)}
          </div>
          <button onClick={load} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 13px", fontSize: 11.5, fontWeight: 600, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 9, color: "#6B7280", cursor: "pointer" }}>
            <RefreshCw size={12} style={loading?{animation:"spin 1s linear infinite"}:{}}/> Refresh
          </button>
          {onLogout && (
            <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 13px", fontSize: 11.5, fontWeight: 600, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 9, color: "#EF4444", cursor: "pointer" }}>
              Logout{username ? ` (${username})` : ""}
            </button>
          )}
        </div>
      </div>

      <div style={{ background: "#fff", borderBottom: "1px solid #E5E7EB", padding: "0 16px", display: "flex", gap: 0, overflowX: "auto" }}>
        {TABS.map((t,i)=><button key={t} onClick={()=>setTab(i)} style={{ padding: "12px 18px", fontSize: 13, fontWeight: 600, border: "none", borderBottom: tab===i?"2.5px solid #3B82F6":"2.5px solid transparent", color: tab===i?"#3B82F6":"#9CA3AF", background: "transparent", cursor: "pointer", whiteSpace: "nowrap" }}>{t}</button>)}
      </div>

      <div className="dashboard-content" style={{ padding: "20px 28px" }}>

        {tab===0 && <>
          <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(185px, 1fr))", gap: 14 }}>
            <Stat icon={Send} label="Total Outreach" value={fmt(agg.total)} sub={`${agg.responded} commented`} color="#3B82F6"/>
            <Stat icon={UserPlus} label="Connections Accepted" value={fmt(agg.connAcc)} sub={`${agg.connPend} pending`} color="#10B981"/>
            <Stat icon={MessageSquare} label="DMs Sent" value={fmt(agg.dmS)} sub={`${agg.dmR} received`} color="#8B5CF6"/>
            <Stat icon={MessageCircle} label="Comments Sent" value={fmt(agg.cS)} sub={`${agg.cR} replies`} color="#06B6D4"/>
            <Stat icon={Flame} label="Hot Leads" value={fmt(agg.hot)} color="#EF4444"/>
            <Stat icon={Users} label="Total Network" value={fmt(agg.rel)} color="#F59E0B"/>
          </div>
          <div className="charts-grid-main" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginTop: 14 }}>
            <Card title="Daily Activity" onExport={()=>csvExport(chartDaily,"daily.csv")}>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartDaily}>
                  <defs>
                    <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.15}/><stop offset="100%" stopColor="#3B82F6" stopOpacity={0}/></linearGradient>
                    <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.15}/><stop offset="100%" stopColor="#10B981" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6"/>
                  <XAxis dataKey="dl" tick={{fill:"#9CA3AF",fontSize:11}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fill:"#9CA3AF",fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip {...tt}/><Legend wrapperStyle={{fontSize:11}}/>
                  <Area type="monotone" dataKey="invites_sent" stroke="#3B82F6" fill="url(#ga)" name="Invites" strokeWidth={2}/>
                  <Area type="monotone" dataKey="invites_accepted" stroke="#10B981" fill="url(#gb)" name="Accepted" strokeWidth={2}/>
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Lead Distribution">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart><Pie data={leadDist} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={50} paddingAngle={4} strokeWidth={0}>{leadDist.map((e,i)=><Cell key={i} fill={e.color}/>)}</Pie><Tooltip {...tt}/><Legend wrapperStyle={{fontSize:11}}/></PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div className="charts-grid-secondary" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
            <Card title="DMs: Sent vs Received">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartDaily} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6"/><XAxis dataKey="dl" tick={{fill:"#9CA3AF",fontSize:11}} axisLine={false} tickLine={false}/><YAxis tick={{fill:"#9CA3AF",fontSize:11}} axisLine={false} tickLine={false}/><Tooltip {...tt}/><Legend wrapperStyle={{fontSize:11}}/>
                  <Bar dataKey="dms_sent" fill="#8B5CF6" name="Sent" radius={[4,4,0,0]}/><Bar dataKey="dms_received" fill="#06B6D4" name="Received" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="New Connections Over Time">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={chartDaily}>
                  <defs><linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.2}/><stop offset="100%" stopColor="#F59E0B" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6"/><XAxis dataKey="dl" tick={{fill:"#9CA3AF",fontSize:11}} axisLine={false} tickLine={false}/><YAxis tick={{fill:"#9CA3AF",fontSize:11}} axisLine={false} tickLine={false}/><Tooltip {...tt}/>
                  <Area type="monotone" dataKey="new_connections" stroke="#F59E0B" fill="url(#gc)" name="New Connections" strokeWidth={2}/>
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>}

        {tab===1 && <>
          <div className="stats-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))", gap: 14, marginBottom: 14 }}>
            <Stat icon={Flame} label="Hot Leads" value={fmt(agg.hot)} sub="Inbound DM" color="#EF4444"/>
            <Stat icon={Zap} label="Warm Leads" value={fmt(agg.warm)} sub="Accepted" color="#F59E0B"/>
            <Stat icon={UserPlus} label="New Leads" value={fmt(agg.newL)} sub="Comment sent" color="#3B82F6"/>
            <Stat icon={AlertTriangle} label="Stale Leads" value={fmt(agg.stale)} sub="2+ days no accept" color="#6B7280"/>
          </div>
          <Card title="All Leads" onExport={()=>csvExport(data.posts,"leads.csv")}>
            <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead><tr style={{ background: "#F9FAFB", position: "sticky", top: 0, zIndex: 1 }}>
                  {["Lead","Author","Status","Connection","DMs","Reply","Post","Actions"].map(h=><th key={h} style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #E5E7EB", fontSize: 11.5 }}>{h}</th>)}
                </tr></thead>
                <tbody>{data.posts.map((p,i)=>(
                  <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }} onMouseEnter={e=>e.currentTarget.style.background="#F9FAFB"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{ padding: "10px 12px" }}><LeadBadge post={p}/></td>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "#374151" }}>{p["Author Name"]||p.Profile||"—"}</td>
                    <td style={{ padding: "10px 12px" }}><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: p.Status==="Responded"?"#DCFCE7":"#DBEAFE", color: p.Status==="Responded"?"#16A34A":"#2563EB" }}>{p.Status||"—"}</span></td>
                    <td style={{ padding: "10px 12px" }}><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: (p["Connection Status"]||"")==="accepted"?"#DCFCE7":"#FEF3C7", color: (p["Connection Status"]||"")==="accepted"?"#16A34A":"#D97706" }}>{p["Connection Status"]||"—"}</span></td>
                    <td style={{ padding: "10px 12px", color: "#6B7280" }}>{p["DM's"]||"—"}</td>
                    <td style={{ padding: "10px 12px" }}>{p["Reply Status"]?<span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: p["Reply Status"]==="Replied"?"#FEF2F2":"#F3F4F6", color: p["Reply Status"]==="Replied"?"#EF4444":"#6B7280" }}>{p["Reply Status"]}</span>:"—"}</td>
                    <td style={{ padding: "10px 12px", color: "#9CA3AF", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(p.Post||"").substring(0,55)}…</td>
                    <td style={{ padding: "10px 12px" }}>{p.postURL&&<a href={p.postURL} target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, textDecoration: "none" }}><ExternalLink size={12}/> Post</a>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
        </>}

        {tab===2 && <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(185px, 1fr))", gap: 14, marginBottom: 14 }}>
            <Stat icon={Send} label="Invites Sent" value={fmt(data.connections.filter(c=>c.status==="sent"||c.status==="pending").length)} color="#3B82F6"/>
            <Stat icon={Users} label="Accepted" value={fmt(data.connections.filter(c=>c.status==="accepted").length)} color="#10B981"/>
            <Stat icon={TrendingUp} label="Accept Rate" value={pct(data.connections.filter(c=>c.status==="accepted").length,data.connections.length)} color="#06B6D4"/>
            <Stat icon={Users} label="Total Network" value={fmt(agg.rel)} color="#F59E0B"/>
          </div>
          <Card title="Connection Requests" onExport={()=>csvExport(data.connections,"connections.csv")}>
            <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead><tr style={{ background: "#F9FAFB", position: "sticky", top: 0 }}>
                  {["Name","Profile","Status","Sent","Accepted"].map(h=><th key={h} style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #E5E7EB", fontSize: 11.5 }}>{h}</th>)}
                </tr></thead>
                <tbody>{data.connections.slice(0,50).map((c,i)=>(
                  <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "#374151" }}>{c.recipient_name||"—"}</td>
                    <td style={{ padding: "10px 12px" }}>{c.recipient_profile_url?<a href={c.recipient_profile_url} target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6", fontSize: 11, textDecoration: "none" }}>View</a>:"—"}</td>
                    <td style={{ padding: "10px 12px" }}><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: c.status==="accepted"?"#DCFCE7":"#FEF3C7", color: c.status==="accepted"?"#16A34A":"#D97706" }}>{c.status||"—"}</span></td>
                    <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{sd(c.sent_date)}</td>
                    <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{c.accepted_date&&c.accepted_date!=="pending"?sd(c.accepted_date):"—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
        </>}

        {tab===3 && <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(185px, 1fr))", gap: 14, marginBottom: 14 }}>
            <Stat icon={Send} label="DMs Sent" value={fmt(agg.dmS)} color="#8B5CF6"/>
            <Stat icon={MessageSquare} label="DMs Received" value={fmt(agg.dmR)} color="#06B6D4"/>
            <Stat icon={TrendingUp} label="Reply Rate" value={pct(agg.dmR,agg.dmS)} color="#10B981"/>
          </div>
          <Card title="Recent Messages" onExport={()=>csvExport(data.dms,"dms.csv")}>
            <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead><tr style={{ background: "#F9FAFB", position: "sticky", top: 0 }}>
                  {["Attendee","Direction","Message","Date","Reply","Response Time"].map(h=><th key={h} style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #E5E7EB", fontSize: 11.5 }}>{h}</th>)}
                </tr></thead>
                <tbody>{data.dms.slice(0,50).map((m,i)=>(
                  <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "#374151" }}>{m.attendee_name||"—"}</td>
                    <td style={{ padding: "10px 12px" }}><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: m.direction==="sent"?"#EDE9FE":"#CFFAFE", color: m.direction==="sent"?"#7C3AED":"#0891B2" }}>{m.direction||"—"}</span></td>
                    <td style={{ padding: "10px 12px", color: "#6B7280", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(m.message_text||"").substring(0,80)}</td>
                    <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{sd(m.message_date)}</td>
                    <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{m.is_reply?"✓":"—"}</td>
                    <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{m.response_time_mins?m.response_time_mins+"m":"—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
        </>}

        {tab===4 && <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(185px, 1fr))", gap: 14, marginBottom: 14 }}>
            <Stat icon={Activity} label="Posts Processed" value={fmt(agg.total)} color="#3B82F6"/>
            <Stat icon={MessageCircle} label="Comments Sent" value={fmt(agg.responded)} color="#10B981"/>
            <Stat icon={UserPlus} label="Connections Sent" value={fmt(agg.connAcc+agg.connPend)} color="#F59E0B"/>
            <Stat icon={Eye} label="Queued (New)" value={fmt(agg.newPosts)} color="#8B5CF6"/>
          </div>
          <Card title="Outreach Activity Log" onExport={()=>csvExport(data.posts,"outreach.csv")}>
            <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead><tr style={{ background: "#F9FAFB", position: "sticky", top: 0 }}>
                  {["Author","Profile","Comment Sent","Connection","DM Stage","Lead","Go to Post"].map(h=><th key={h} style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #E5E7EB", fontSize: 11.5 }}>{h}</th>)}
                </tr></thead>
                <tbody>{data.posts.filter(p=>p.Status==="Responded").map((p,i)=>(
                  <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }} onMouseEnter={e=>e.currentTarget.style.background="#F9FAFB"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "#374151" }}>{p["Author Name"]||"—"}</td>
                    <td style={{ padding: "10px 12px", color: "#3B82F6" }}>{p.Profile||"—"}</td>
                    <td style={{ padding: "10px 12px", color: "#6B7280", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(p.Reply||"").substring(0,60)}…</td>
                    <td style={{ padding: "10px 12px" }}><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: (p["Connection Status"]||"")==="accepted"?"#DCFCE7":"#FEF3C7", color: (p["Connection Status"]||"")==="accepted"?"#16A34A":"#D97706" }}>{p["Connection Status"]||"—"}</span></td>
                    <td style={{ padding: "10px 12px", color: "#6B7280" }}>{p["DM's"]||"—"}</td>
                    <td style={{ padding: "10px 12px" }}><LeadBadge post={p}/></td>
                    <td style={{ padding: "10px 12px" }}>{p.postURL&&<a href={p.postURL} target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, textDecoration: "none" }}><ExternalLink size={12}/> Open</a>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
        </>}

        {tab===5 && <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(185px, 1fr))", gap: 14, marginBottom: 14 }}>
            <Stat icon={Send} label="Comments Sent" value={fmt(agg.cS)} color="#06B6D4"/>
            <Stat icon={MessageCircle} label="Replies Received" value={fmt(agg.cR)} color="#10B981"/>
            <Stat icon={TrendingUp} label="Reply Rate" value={pct(agg.cR,agg.cS)} color="#F59E0B"/>
          </div>
          <Card title="Comment Threads" onExport={()=>csvExport(data.comments,"comments.csv")}>
            <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead><tr style={{ background: "#F9FAFB", position: "sticky", top: 0 }}>
                  {["Author","Direction","Comment","Date","Reply to Mine","Reactions","Impressions"].map(h=><th key={h} style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #E5E7EB", fontSize: 11.5 }}>{h}</th>)}
                </tr></thead>
                <tbody>{data.comments.map((c,i)=>(
                  <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "#374151" }}>{c.author_name||"—"}</td>
                    <td style={{ padding: "10px 12px" }}><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: c.direction==="sent"?"#DBEAFE":"#DCFCE7", color: c.direction==="sent"?"#2563EB":"#16A34A" }}>{c.direction||"—"}</span></td>
                    <td style={{ padding: "10px 12px", color: "#6B7280", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(c.text||"").substring(0,80)}</td>
                    <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{sd(c.comment_date)}</td>
                    <td style={{ padding: "10px 12px" }}>{c.is_reply_to_mine?<span style={{ color: "#10B981", fontWeight: 600 }}>Yes</span>:"—"}</td>
                    <td style={{ padding: "10px 12px", color: "#6B7280" }}>{c.reactions||0}</td>
                    <td style={{ padding: "10px 12px", color: "#6B7280" }}>{c.impressions||0}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
        </>}
      </div>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#D1D5DB;border-radius:3px}

        @media (max-width: 1024px) {
          .charts-grid-main {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 768px) {
          .dashboard-header {
            padding: 14px 16px !important;
          }
          .dashboard-header h1 {
            font-size: 16px !important;
          }
          .dashboard-header p {
            font-size: 10px !important;
          }
          .header-controls {
            width: 100%;
            justify-content: space-between;
          }
          .dashboard-content {
            padding: 16px !important;
          }
          .stats-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important;
            gap: 10px !important;
          }
          .stats-grid-4 {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important;
            gap: 10px !important;
          }
          .charts-grid-secondary {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 640px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .stats-grid-4 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 480px) {
          .dashboard-header {
            padding: 12px !important;
          }
          .dashboard-header h1 {
            font-size: 14px !important;
          }
          .stats-grid {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .stats-grid-4 {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .dashboard-content {
            padding: 12px !important;
          }
          .stat-card {
            padding: 16px 18px !important;
          }
          .stat-card > div:last-child > div:first-child {
            font-size: 22px !important;
          }
          .stat-card > div:last-child > div:nth-child(2) {
            font-size: 11px !important;
          }
          .stat-card > div:last-child > div:nth-child(3) {
            font-size: 10px !important;
          }
          .card {
            padding: 14px 16px !important;
          }
          .card-header {
            font-size: 13px !important;
          }
          .card-header > span {
            font-size: 13px !important;
          }
          table {
            font-size: 11px !important;
            display: block;
            overflow-x: auto;
            white-space: nowrap;
          }
          table th, table td {
            padding: 8px 6px !important;
            font-size: 10px !important;
          }
          button {
            font-size: 11px !important;
            padding: 5px 10px !important;
          }
        }
      `}</style>
    </div>
  );
}